import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import { AllocationUsageType, ExternalEventAllocationUsageType, ProjectAllocationUsageType } from '@/generated/graphql';
import { Allocation, AllocationPhaseUpdate, LocationType, PhaseTypeDatabase } from './Timeline.types';
import { TimelineAllocationResponse } from './allocation/types';

export const EXECUTION_PHASE_TYPES: readonly PhaseTypeDatabase[] = [
    ProjectAllocationUsageType.Project,
    AllocationUsageType.ShowDays,
    ExternalEventAllocationUsageType.ExternalEvent,
];

export function getTransparencyVariant(
    highlightedVariantId: string,
    variantId: string,
    isCurrent: boolean,
): Allocation['variant'] {
    if (highlightedVariantId === variantId || highlightedVariantId === DEFAULT_HIGHLIGHT_VALUE) {
        if (isCurrent) {
            return 'Solid';
        } else {
            return 'Outer-Semi-Transparent';
        }
    }
    return 'Semi-Transparent';
}

export function getSectionId(locationType: LocationType | 'no-room-type' | 'no-area-type'): string {
    switch (locationType) {
        case 'Hallenausstellungsfläche':
            return 'section-id-hallenausstellungsflächen';

        case 'Boulevardsektion':
            return 'section-id-boulevard-sektionen';

        case 'Passage':
        case 'Passage inkl. Durchfahrtstor':
            return 'section-id-passagen';

        case 'Eingang/Zugang':
            return 'section-id-eingänge-zugänge';

        case 'Service-Center':
            return 'section-id-service-center';

        case 'Konferenzraum-DUMMY':
            return 'section-id-konferenzräume';

        case 'Büro/Besprecher (ZBV intern)':
            return 'section-id-büros-besprecher-intern';

        // These types have no section and are not in TIMELINE_ROOM_TYPES, so they never reach a
        // section row — the ids only keep the exhaustiveness check below satisfied.
        case 'Konferenzraum':
        case 'Büro/Besprecher (ZBV extern)':
        case 'Lager (ZBV intern)':
        case 'Lager (ZBV extern)':
            return 'section-id-unknown-room-type';

        case 'Parkfläche (intern)':
        case 'Parkfläche (extern)':
            return 'section-id-parking';

        case 'Außenfläche (intern)':
        case 'Außenfläche (extern)':
            return 'section-id-outdoor-area';

        case 'Geländezufahrt/-zugang':
            return 'section-id-entrance';

        case 'Fahrstraße (intern)':
        case 'Fahrstraße (extern)':
        case 'Fahrstraße (Parkdeck)':
        case 'Rampe (Parkdeck)':
            return 'section-id-road';

        case 'no-room-type':
            return 'section-id-unknown-room-type';

        case 'no-area-type':
            return 'section-id-unknown-area-type';

        default:
            throw new Error(`Unknown location type: ${locationType satisfies never}`);
    }
}

export function validateAllocationUpdateResponse(
    allocation: TimelineAllocationResponse | null | undefined,
    remainingPhases: (AllocationPhaseUpdate & { dayOffsetCount: number })[],
): AllocationPhaseUpdate[] {
    if (!allocation) {
        throw new Error('No response from allocation update');
    }
    const phases = allocation.phases.nodes;
    if (!phases) {
        throw new Error('Updated phases are missing in response');
    }
    if (phases.length !== remainingPhases.length) {
        throw new Error('Unexpected mismatch of frontend phases length and updated phases in response');
    }

    return remainingPhases.map((phase, index) => ({
        ...phase,
        id: phases[index].id,
        cellSpan: phase.cellSpanBefore + phase.cellSpan + phase.cellSpanAfter,
        cellSpanBefore: 0,
        cellSpanAfter: 0,
        specialization: phase.newSpecialization,
    }));
}
